import { t } from "i18next";
import * as React from "react";
import { useState } from "react";
import {
  AddButton,
  ButtonsContainer,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { Patch, handleError } from "../../../utils/fetch.util";
import { UserContext } from "../../../contexts/user.context";
import LoadingData from "../../loadingData/loadingData.component";
import { toast } from "react-toastify";
import { FiEye, FiEyeOff } from "react-icons/fi";

interface ChangePasswordModelProps {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  open: boolean;
}

const ChangePasswordModel: React.FC<ChangePasswordModelProps> = ({
  open,
  setOpen,
}) => {
  const { getValidToken } = React.useContext(UserContext);
  const [oldPassword, setOldPassword] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  
  // Password visibility toggles
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState<boolean>(false);

  // Validation helper
  const validatePasswords = () => {
    if (!oldPassword.trim()) {
      toast.error("Please enter your current password");
      return false;
    }
    
    if (!password.trim()) {
      toast.error("Please enter a new password");
      return false;
    }
    
    if (password.length < 8) {
      toast.error("New password must be at least 8 characters long");
      return false;
    }
    
    if (password !== confirmPassword) {
      toast.error("New password and confirmation do not match");
      return false;
    }
    
    if (oldPassword === password) {
      toast.error("New password must be different from current password");
      return false;
    }
    
    return true;
  };

  // Change password handler
  const handleChangePassword = async (e: any) => {
    e.preventDefault();
    
    if (!validatePasswords()) {
      return;
    }

    setLoading(true);

    try {
      const res = await Patch(
        "asdc-admin/update-admin-password",
        {
          oldPassword,
          password,
          confirmPassword,
        },
        getValidToken(),
      );

      if (res.error || !res.data) {
        handleError(res);
        return;
      }

      toast.success("Password updated successfully!");
      
      // Reset form
      setOldPassword("");
      setPassword("");
      setConfirmPassword("");
      setOpen(false);
    } catch (error) {
      toast.error("An error occurred while updating password");
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <LoadingData />;

  return (
    <div
      className={`toggle_server_model fixed top-0 left-0 w-full h-full bg-[#00000075] flex items-center justify-center !z-[99999] ${
        open ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="fixed top-0 left-0 w-full h-[100vh] cursor-pointer"
        onClick={() => setOpen(false)}
      ></div>
      <form
        className={`bg-white w-full max-w-[500px] p-8 relative z-[9999] rounded-lg shadow-xl`}
        onSubmit={handleChangePassword}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">{t("change_password")}</h2>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Current Password */}
        <div className="flex flex-col gap-2 mb-5">
          <label className="text-sm font-medium text-gray-700" htmlFor="oldPassword">
            {t("current_password") || "Current Password"} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="oldPassword"
              onChange={(e) => setOldPassword(e.target.value)}
              value={oldPassword}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              type={showOldPassword ? "text" : "password"}
              placeholder="Enter your current password"
              required
            />
            <button
              type="button"
              onClick={() => setShowOldPassword(!showOldPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showOldPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
        </div>

        {/* New Password */}
        <div className="flex flex-col gap-2 mb-5">
          <label className="text-sm font-medium text-gray-700" htmlFor="newPassword">
            {t("new_password") || "New Password"} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="newPassword"
              onChange={(e) => setPassword(e.target.value)}
              value={password}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              type={showNewPassword ? "text" : "password"}
              placeholder="Enter new password (min. 8 characters)"
              required
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showNewPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {password && password.length < 8 && (
            <p className="text-xs text-red-500">Password must be at least 8 characters long</p>
          )}
        </div>

        {/* Confirm New Password */}
        <div className="flex flex-col gap-2 mb-6">
          <label className="text-sm font-medium text-gray-700" htmlFor="confirmPassword">
            {t("confirm_new_password") || "Confirm New Password"} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              id="confirmPassword"
              onChange={(e) => setConfirmPassword(e.target.value)}
              value={confirmPassword}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-10"
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your new password"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              {showConfirmPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          </div>
          {confirmPassword && password !== confirmPassword && (
            <p className="text-xs text-red-500">Passwords do not match</p>
          )}
        </div>

        <ButtonsContainer style={{ gap: "1rem", marginTop: "2rem" }}>
          <AddButton
            type="submit"
            isLoading={loading}
            disabled={loading || !oldPassword || !password || !confirmPassword || password !== confirmPassword}
            className="flex-1 !bg-blue-400"
          >
            {loading ? "Updating..." : t("update_password") || "Update Password"}
          </AddButton>
          <CancelButton
            type="button"
            onClick={() => setOpen(false)}
            disabled={loading}
            className="flex-1"
          >
            {t("cancelBtn") || "Cancel"}
          </CancelButton>
        </ButtonsContainer>
      </form>
    </div>
  );
};

export default ChangePasswordModel;
